// QuoteTemplate — design spec §5.
// Large pull quote OR oversized stat. Reference: Apple "1 billion" stat slide
// + Stripe testimonial hero + Linear homepage testimonial.

import { motion } from 'framer-motion';
import { Stage } from '../stage/Stage';
import { Orb } from '../stage/Orb';
import { Eyebrow } from '../stage/Eyebrow';
import { fadeUp, stagger } from '../motion/variants';
import type { SceneContent } from '../types';

interface QuoteProps {
  content: SceneContent;
}

export function QuoteTemplate({ content }: QuoteProps) {
  const { headline = '', body, eyebrow, citation, mode = 'stat' } = content;

  return (
    <Stage>
      {/* Orb anchor — bottom-right, 40px (smallest of all templates) per §5g */}
      <div className="absolute bottom-12 right-12 max-md:bottom-6 max-md:right-6">
        <Orb size={40} />
      </div>

      <motion.div
        initial="hidden"
        animate="show"
        exit="exit"
        variants={stagger(0.15)}
        className="m-auto w-full max-w-[1280px] flex flex-col items-center text-center gap-10"
      >
        {eyebrow && (
          <motion.div variants={fadeUp}>
            <Eyebrow>{eyebrow}</Eyebrow>
          </motion.div>
        )}

        {mode === 'stat' ? (
          <motion.span
            variants={{
              hidden: { opacity: 0, y: 32, filter: 'blur(12px)' },
              show: {
                opacity: 1, y: 0, filter: 'blur(0px)',
                transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] },
              },
            }}
            className="font-display text-numeric font-semibold tabular-nums tracking-[-0.05em] text-fg"
          >
            {headline}
          </motion.span>
        ) : (
          <motion.blockquote
            variants={stagger(0.2)}
            className="text-display-2 font-display text-balance max-w-[22ch] leading-[1.05] text-fg"
          >
            <motion.span variants={fadeUp} aria-hidden className="text-accent">“</motion.span>
            <motion.span variants={fadeUp}>{headline}</motion.span>
            <motion.span variants={fadeUp} aria-hidden className="text-accent">”</motion.span>
          </motion.blockquote>
        )}

        {body && (
          <motion.p variants={fadeUp} className="text-subhead text-fg-mute max-w-[40ch]">
            {body}
          </motion.p>
        )}

        {citation && (
          <motion.cite
            variants={fadeUp}
            className="not-italic text-caption font-mono text-fg-mute mt-4 before:content-['—_'] before:text-fg-faint"
          >
            {citation}
          </motion.cite>
        )}
      </motion.div>
    </Stage>
  );
}
